<template>
	<div ref="container"></div>
</template>

<script>
import GiftWidget from '@/widgets/GiftWidget';
import SoundManager from '@/SoundManager';
import MockSoundManager from '@/MockSoundManager';
import VampiVoiceClient from '@/VampiVoiceClient';
import MmlApiClient from '@/MmlApiClient';
import previewConfig from '@/previewConfig';

// Lazy singletons — created only when audio preview is first enabled,
// then reused across widget reinits so we don't create multiple AudioContexts.
let liveSoundManager = null;

function getLiveSoundManager() {
	return liveSoundManager ||= new SoundManager({
		voiceApi:  new VampiVoiceClient(process.env.VUE_APP_VAMPIVOICEAPIURL, process.env.VUE_APP_VAMPIVOICEAPIKEY),
		mmlApi:    new MmlApiClient(process.env.VUE_APP_VAMPIMMLAPIURL, process.env.VUE_APP_VAMPIMMLAPIKEY),
	});
}

let msgIdCounter = 0;

const AVATAR_COLORS = [
	'#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
	'#3498db', '#9b59b6', '#e91e63', '#00bcd4', '#ff5722',
];

const GIFT_VALUES = [1, 2, 5, 10, 20, 50, 100];

const mockSoundManager = new MockSoundManager();

function hashCode(str) {
	let h = 0;
	for(let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	return Math.abs(h);
}

function generateInitialsAvatar(username) {
	const initials = username.slice(0, 2).toUpperCase();
	const color = AVATAR_COLORS[hashCode(username) % AVATAR_COLORS.length];
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="${color}"/><text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="13" font-weight="bold" fill="white">${initials}</text></svg>`;
	return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function pickRandom(arr) {
	return arr[Math.floor(Math.random() * arr.length)];
}

export default {
	name: 'GiftWidgetPreview',
	props: {
		settings: { type: Object, default: () => ({}) },
		speed: { type: Number, default: 0.5 },
		previewAudio: { type: Boolean, default: false },
		realData: { type: Boolean, default: false },
	},
	data() {
		return {
			services: [],
			voices: [],
			previewIntervalId: null,
		};
	},
	watch: {
		settings: {
			deep: true,
			handler(s) { this._applySettings(s); },
		},
		speed() {
			if(!this.realData) { this._stopPreview(); this._startPreview(); }
		},
		previewAudio() {
			this._destroyWidget();
			this._initWidget();
		},
		realData() {
			this._stopPreview();
			this._destroyWidget();
			this._initWidget();
		},
	},
	mounted() {
		this._initWidget();

		this.$api.get('channels')
			.then(channels => {
				const seen = new Set();
				this.services = channels.filter(c => {
					if(!c.service_icon_url || seen.has(c.service_id)) return false;
					seen.add(c.service_id);
					return true;
				});
			})
			.catch(() => {});

		this.$api.get('userVoices', { default: 1, limit: 100 })
			.then(result => { this.voices = result.results || []; })
			.catch(() => {});
	},
	beforeDestroy() {
		this._stopPreview();
		this._destroyWidget();
	},
	methods: {
		async _initWidget() {
			let hash = 'preview';
			try {
				const user = await this.$api.get('userData');
				if(user?.widget_hash) hash = user.widget_hash;
			} catch(e) { /* ignore */ }

			if(!this.$refs.container) return;

			const [eventTypes, giftTypesResponse, giftThresholds, textReplacements, serviceIcons, userData] = await Promise.all([
				this.$api.get(`widget/${encodeURIComponent(hash)}/eventTypes`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/giftTypes`).catch(() => ({ results: [] })),
				this.$api.get(`widget/${encodeURIComponent(hash)}/giftThresholds`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/textReplacements`).catch(() => ({})),
				this.$api.get(`widget/${encodeURIComponent(hash)}/services`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/userData`).catch(() => null),
			]);

			if(!this.$refs.container) return;

			const container = this.$refs.container;
			container.style.display = 'block';
			container.style.width = '100%';

			// In realData mode, forward alertStatus events to the API
			if(this.realData) {
				container.addEventListener('alertStatus', ({ detail }) => {
					this.$api.post(
						`widget/${encodeURIComponent(hash)}/alert/${encodeURIComponent(detail.dbId)}/status`,
						{ status: detail.status, ...(detail.err ? { err: detail.err } : {}) }
					).catch(() => {});
				});
			}

			this.widget = new GiftWidget({
				container,
				soundManager: this.previewAudio ? getLiveSoundManager() : mockSoundManager,
				baseUrl: this.$baseUrl,
			});

			const alerts = this.realData
				? await this.$api.get(`widget/${encodeURIComponent(hash)}/alerts`, { readyOnly: true }).catch(() => [])
				: [];

			await this.widget.init({
				settings: { ...this.settings },
				eventTypes,
				giftTypes: giftTypesResponse?.results || [],
				giftThresholds,
				textReplacements,
				serviceIcons,
				userData,
				alerts,
			});

			if(this.realData) {
				this._previewSubId = this.$socket.subscribe(hash, {
					reconnect: () => { this._stopPreview(); this._destroyWidget(); this._initWidget(); },
					userWidgetAlert:      data => this.widget?._onAlert(data),
					userWidgetAlertDelete: () => {},
					alertQueuePause:  () => this.widget?.queue.pause(),
					alertQueueResume: () => this.widget?.queue.resume(),
					alertQueueSkip:   () => this.widget?.queue.skip(),
				});
			} else {
				this._startPreview();
			}
		},
		_destroyWidget() {
			if(this._previewSubId != null) { this.$socket.unsubscribe(this._previewSubId); this._previewSubId = null; }
			if(this.widget) {
				const cancelled = this.widget.queue.cancel(() => true);
				for(const item of cancelled) {
					item.sounds?.forEach(s => s?.stop?.());
					item.mediaSound?.stop?.();
				}
				this.widget.queue.skip();
				this.widget.destroy();
				this.widget = null;
				if(this.$refs.container) this.$refs.container.innerHTML = '';
			}
		},
		_applySettings(s) {
			if(!this.widget) return;
			this.widget.applySettings({ ...s });
		},
		_startPreview() {
			this._addFakeAlert();
			this.previewIntervalId = setInterval(() => this._addFakeAlert(), 1500 + (1 - this.speed) * 10500);
		},
		_stopPreview() {
			if(!this.previewIntervalId) return;
			clearInterval(this.previewIntervalId);
			this.previewIntervalId = null;
		},
		_addFakeAlert() {
			if(!this.widget) return;
			if(this.widget.queue.size > 5) return;

			const username = pickRandom(previewConfig.usernames);
			const value = pickRandom(GIFT_VALUES);
			const hasMessage = Math.random() < 0.5;
			const messageText = hasMessage ? pickRandom(previewConfig.chatMessages) : null;

			const avatarUrl = previewConfig.avatarUrls?.length
				? pickRandom(previewConfig.avatarUrls)
				: generateInitialsAvatar(username);

			let serviceId = null;
			let serviceIconUrl = null;
			let serviceName = null;
			if(this.services.length > 0) {
				const svc = pickRandom(this.services);
				serviceId = svc.service_id;
				serviceIconUrl = svc.service_icon_url;
				serviceName = svc.service_name || svc.service_id;
			}

			const voice = this.voices.length > 0 ? pickRandom(this.voices) : null;

			this.widget._onAlert({
				message: {
					id: `preview-${++msgIdCounter}`,
					serviceId: serviceId || 'preview',
					serviceName,
					serviceIconUrl,
					sender: {
						displayName: username,
						avatar: avatarUrl,
						isSelf: false,
						settings: {
							voice_provider: voice?.provider,
							voice_id: voice?.id,
						}
					},
					type: 'gift',
					giftType: 'preview',
					value,
					currency: 'USD',
					spans: messageText ? [{ text: messageText }] : [],
				},
				sender: { isSelf: false },
			});
		},
	},
};
</script>
